<template>
    <div>
        <div class="grid grid-cols-2 gap-3"
             :class="{'pr-4 md:pr-2 overflow-y-auto overscroll-none scrollbar-thin scrollbar-track-gray-100 scrollbar-thumb-indigo-100' : hasFormErrors }"
             style="max-height: inherit">
            <div class="col-span-2">
                <div class="flex items-center">
                    <label class="font-medium text-md">Asset</label>
                    <transition name="fade">
                        <el-popover trigger="hover" placement="top" v-if="hasAssetNotes">
                            <div class="text-gray-800">{{ selectedAsset.notes }}</div>
                            <div slot="reference">
                                <icon-annotation class="w-4 h-4 ml-1 mb-1 text-lg text-indigo-600"></icon-annotation>
                            </div>
                        </el-popover>
                    </transition>
                </div>

                <remote-select
                    v-model="selectedAsset"
                    @focus="searchAssets"
                    :remote-method="searchAssets"
                    :loading="loading"
                    :data="assets"
                    item-label="name"
                    placeholder="Start typing to search assets, addresses or linked contacts">
                    <template slot-scope="props">
                        <assets-drop-down-item :asset="props.item"></assets-drop-down-item>
                    </template>
                </remote-select>

                <div class="text-red-500 text-xs break-normal mt-1" v-show="form.errors.has('ticket_details.asset_id')">
                    {{ form.errors.get("ticket_details.asset_id") }}
                </div>
            </div>


            <!-- Contact Group -->
            <div class="col-span-2" v-if="selectedAsset && hasFeature('feat_leases')">
                <label class="font-medium text-md">Select Lease</label>
                <remote-select
                    v-model="selectedContactGroup"
                    @focus="fetchContactGroups"
                    :remote-method="fetchContactGroups"
                    :loading="loading"
                    :data="contactGroups"
                    item-label="compiled_label"
                    placeholder="Select Lease">

                    <template slot-scope="props">
                        <div class="w-full max-w-0 text-sm font-medium text-gray-900 leading-snug py-3">
                            <div class="font-semibold flex items-center justify-between">
                                <div>
                                    {{ props.item.start_date | date }}
                                    {{ props.item.end_date ? "to " + $options.filters.date(props.item.end_date) : "to no end date" }}
                                </div>
                                <div class="mt-1 truncate text-indigo-600 font-bold">{{ props.item.secondary_identifier_id }}</div>
                            </div>
                            <div v-if="props.item.contacts && props.item.contacts.length > 0">
                                <ul class="text-gray-400 font-normal">
                                    <li v-for="contact in props.item.contacts" class="flex items-center">
                                        <icon-user class="w-5 h-5 inline-block pr-2 pt-1" :filled="contact.pivot.primary"></icon-user>
                                        <span class="text-sm">{{ contact.full_name }}</span>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </template>
                </remote-select>
                <span class="invalid-feedback" :class="{'d-block': form.errors.has('contact_group_id')}"
                      v-show="form.errors.has('contact_group_id')">
                    {{ form.errors.get("contact_group_id") }}
                </span>
            </div>

            <!-- Individual Tenant Selection -->
            <div v-if="!this.selectedContactGroup && this.selectedAsset" class="col-span-2">
                <label class="font-medium text-md">Tenant</label>
                <remote-select
                    v-model="selectedTenant"
                    @focus="fetchTenants($event, false)"
                    :remote-method="fetchTenants"
                    :loading="loadingTenants"
                    :data="tenants"
                    item-label="full_name"
                    placeholder="Start typing to search tenants">
                    <template slot-scope="props">
                        <div class="flex items-center">
                            <span>{{ props.item.full_name }}</span>
                            <div class="text-xs">
                                <span class="text-gray-500 bg-gray-200 ml-2 rounded-lg px-2 " v-if="props.item.archived_at !== null">Archived</span>
                            </div>
                        </div>
                    </template>
                </remote-select>
                <span class="text-red-500 text-xs break-normal mt-1" v-show="form.errors.has('ticket_details.tenant_id')">
                    {{ form.errors.get("ticket_details.tenant_id") }}
                </span>
            </div>

            <!-- Assign To -->
            <div class="col-span-2">
                <label class="font-medium text-md">Assign To</label>
                <user-input-field v-model="assignedToUser" :portfolio-id="selectedAsset ? selectedAsset.portfolio_id : null"
                                  @input="setAssignedToUser">
                </user-input-field>
                <div class="text-red-500 text-xs break-normal mt-1" v-show="form.errors.has('ticket_details.assigned_to')">
                    {{ form.errors.get("ticket_details.assigned_to") }}
                </div>
            </div>

            <!-- Priority -->
            <div class="col-span-2 md:col-span-1">
                <label class="font-medium text-md">Priority</label>
                <simple-radio-group-blocks :labels="radioButtonOptions" group-name="priority" :background-colours="['green-100', 'indigo-100', 'red-100']"
                                           :text-colours="['green-600', 'indigo-600', 'red-600']"
                                           :default-selected-item-index="1" v-model="form.ticket_details.priority">
                </simple-radio-group-blocks>
            </div>

            <!-- Report Method -->
            <div class="col-span-2 md:col-span-1">
                <label class="font-medium text-md">Reporting Method</label>
                <el-select
                    class="w-full"
                    v-model="form.ticket_details.report_method"
                    placeholder="Select Reporting Source">
                    <el-option
                        v-for="reportMethod in reportingMethods"
                        :value="reportMethod"
                        :label="reportMethod"
                        :key="reportMethod"
                    ></el-option>
                </el-select>
                <div class="text-red-500 text-xs break-normal mt-1" v-show="form.errors.has('report_method')">
                    {{ form.errors.get("report_method") }}
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import AssetSearch from "../../../mixins/asset-search";
import SimpleRadioGroupBlocks from "../../library/SimpleRadioGroupBlocks";

export default {
    name: "TicketCreateModalSelectDetails",
    components: {SimpleRadioGroupBlocks},
    mixins: [AssetSearch],

    props: {
        form: {
            required: true,
            type: Object,
        },
        reportingMethods: {
            required: true,
            type: Array,
            Description: "The reporting methods to choose from"
        },
        asset: {
            required: false,
            type: Object,
            default: null,
        },
        tenant: {
            required: false,
            type: Object,
            default: null,
        },
        contactGroup: {
            required: false,
            type: Object,
            default: null
        },
        user: {
            required: false,
            type: Object,
            default: null,
        },
    },

    data () {
        return {
            selectedAsset: this.asset,
            selectedTenant: this.tenant,
            selectedContactGroup: null,
            assignedToUser: this.user,
            tenants: [],
            contactGroups: [],
            loadingTenants: false,
            landlord: [],
            loadingLandlords: false,
            radioButtonOptions: ["Low", "Medium", "High"],
            hasFormErrors: false,
        };
    },

    computed: {
        showTenantField () {
            return this.selectedAsset !== null;
        },

        hasAssetNotes () {
            return this.selectedAsset && this.selectedAsset.notes;
        },
    },

    watch: {
        asset (newAsset) {
            this.selectedAsset = newAsset;
        },

        selectedAsset (asset) {
            this.selectedTenant = null;
            this.selectedContactGroup = null;
            this.$emit("asset-changed", asset);
            if (asset) {
                if (!this.hasFeature("feat_leases")) {
                    this.fetchTenants();
                }
                this.fetchLandlords();
            }
        },

        selectedContactGroup (contactGroup = null) {
            if (contactGroup && !contactGroup.archived_at && !contactGroup.deleted_at) {
                let index = contactGroup.contacts.findIndex(contact => contact.pivot.primary == true && contact.pivot.role === "tenant");
                if (index !== -1) {
                    this.selectedTenant = contactGroup.contacts[index];
                }
            }

            this.$emit("contact-group-changed", contactGroup);
        },

        tenant (tenant) {
            this.selectedTenant = tenant;
        },

        selectedTenant (tenant) {
            this.$emit("tenant-changed", tenant);
        },

        user (user) {
            this.assignedToUser = user;
        },

        contactGroup (contactGroup) {
            this.selectedContactGroup = contactGroup;
            if (this.selectedContactGroup) {
                this.fetchContactGroups();
            }
        },

        "form.errors.errors": function (val) {
            // Description is on the next section
            if (Object.keys(val).toString().startsWith("ticket_details.description")) {
                return;
            }
            this.hasFormErrors = Object.keys(val).length > 0;
        },

        "form.ticket_details.report_method": function (val) {
            let reporter;
            switch (val) {
                case ("Inspection"):
                    reporter = "Inspector";
                    break;
                case ("RedRabbit Online"):
                case ("Email"):
                case ("SMS"):
                case ("Website"):
                default:
                    reporter = "Tenant";
                    break;
            }
            this.$set(this.form.ticket_details, "reporter", reporter);
        }
    },

    methods: {
        setAssignedToUser (user) {
            this.$emit("assigned-changed", user);
        },

        fetchContactGroups: _.debounce(function () {
            this.loading = true;

            axios.get("/contact-groups/asset/" + this.selectedAsset.id, {})
            .then(response => {
                this.loading = false;
                this.contactGroups = response.data;
            })
            .catch(errors => {
                this.loading = false;
                toastr.error(errors.message);
            });
        }, 300),

        fetchTenants: _.debounce(function (value, autoSelect = true) {
            if (!this.selectedAsset) {
                return;
            }

            this.loadingTenants = true;
            this.tenants = [];
            axios.get("/contacts/asset/" + this.selectedAsset.id, {
                params: {
                    filter: value,
                    archived: "any",
                    role: "tenant"
                }
            })
            .then(response => {
                // We have to check if the feature is enabled or not
                // Because the response structure looks different when this feature is enabled
                if (this.hasFeature("release_view_asset_rework")) {
                    this.tenants = response.data.data;
                } else {
                    this.tenants = response.data;
                }

                if (!(value && value.length > 0) && autoSelect && !this.selectedTenant && !this.hasFeature("feat_leases")) {
                    this.selectedTenant = this.tenants.find(tenant => tenant.pivot.primary == true);
                }
                this.loadingTenants = false;
            })
            .catch(errors => {
                this.loadingTenants = false;
                toastr.error(errors.message);
            });
        }, 300),

        fetchLandlords () {
            this.loadingLandlords = true;
            axios.get("/contacts/asset/" + this.selectedAsset.id, {
                params: {
                    archived: false,
                    role: "landlord"
                }
            })
            .then(response => {
                if (this.hasFeature("release_view_asset_rework")) {
                    this.landlords = response.data.data;
                } else {
                    this.landlords = response.data;
                }
                this.$emit("landlord-changed", this.landlords.find(landlord => landlord.pivot.primary == true));
                this.loadingLandlords = false;
            })
            .catch(errors => {
                this.loadingLandlords = false;
                toastr.error(errors.message);
            });
        },
    }
};
</script>
